import React, { useState, useEffect } from 'react'
import {
  Modal,
  Drawer,
  Button,
  Space,
  Typography,
  Input,
  Card,
  Row,
  Col,
  message,
  Spin,
  Form,
  Divider
} from 'antd'
import { SearchOutlined, CheckOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import api from '../utils/api'
import { useAuth } from '../hooks/AuthContext'
import { useCallback, useMemo } from 'react'

const { Title, Text } = Typography

// 響應式容器
const ResponsiveContainer = styled.div`
  @media (max-width: 768px) {
    .desktop-only {
      display: none;
    }
  }

  @media (min-width: 769px) {
    .mobile-only {
      display: none;
    }
  }
`

// 服務卡片樣式
const ServiceCard = styled(Card)`
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${(props) => (props.selected ? '#d5728a' : '#ffffff')};
  background: ${(props) => (props.selected ? '#fef7f7' : '#f8f9fa')};
  border-radius: 12px;
  position: relative;

  &:hover {
    border-color: #d5728a;
  }

  .ant-card-body {
    padding: 10px;
  }
`

// 服務信息樣式
const ServiceInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const ServiceName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => (props.selected ? '#d5728a' : '#000')};
  flex: 1;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const ServicePrice = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => (props.selected ? '#d5728a' : '#d5728a')};
  margin-left: 12px;
  @media (max-width: 768px) {
    font-size: 18px;
  }
`

const ServiceDuration = styled.div`
  font-size: 12px;
  color: ${(props) => (props.selected ? '#d5728a' : '#878787')};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const SelectionIndicator = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #d5728a;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
`

// 總計卡片樣式
const TotalCard = styled(Card)`
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 20px;
  border: none;

  .ant-card-body {
    padding: 15px !important;
  }
`

const TotalInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const TotalLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
`

const TotalValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #d5728a;
`

// 搜索框樣式
const SearchContainer = styled.div`
  margin-bottom: 20px;

  .ant-input {
    border-radius: 12px;
    padding: 12px 16px;
    font-size: 14px;
    background: #f8f9fa;

    &:focus {
      border-color: #d5728a;
      box-shadow: 0 0 0 2px rgba(213, 114, 138, 0.2);
    }
  }
`

// 分類標題樣式
const CategoryTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 24px 0 16px 0;
`

const ServiceSelector = ({
  visible,
  onClose,
  onServicesSelected,
  currentServices = [],
  currentAdditionalServices = [],
  bookingId = null,
  onPriceCalculation = null
}) => {
  const [form] = Form.useForm()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [services, setServices] = useState([])
  const [additionalServices, setAdditionalServices] = useState([])
  // 主要/額外服務多選
  const [selectedMainServices, setSelectedMainServices] = useState([])
  const [selectedAdditionalServices, setSelectedAdditionalServices] = useState(
    []
  )
  const [searchKeyword, setSearchKeyword] = useState('')
  const [isMobile, setIsMobile] = useState(false)
  const [pricePreview, setPricePreview] = useState(null)
  const [showPricePreview, setShowPricePreview] = useState(false)

  const businessId = user?.businessId || user?.business?._id

  // 檢測屏幕尺寸
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)

    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  // 初始化已選服務（參考 App 端實現）
  const initializeSelection = useCallback(() => {
    console.log('=== ServiceSelector initializeSelection ===')
    console.log('currentServices:', currentServices)
    console.log('services.length:', services.length)
    console.log('additionalServices.length:', additionalServices.length)

    if (!currentServices || currentServices.length === 0) {
      console.log('currentServices 為空，清空選擇')
      setSelectedMainServices([])
      setSelectedAdditionalServices([])
      return
    }

    // 從完整的服務列表中查找匹配的主要服務
    console.log(
      'currentServices 詳細內容:',
      JSON.stringify(currentServices, null, 2)
    )

    // 如果 category 不是 'main' 或 'additional'，根據 serviceId 判斷
    const servicesWithCorrectCategory = currentServices.map((s) => {
      const serviceId = s.serviceId || s._id || s.id

      // 如果 category 已經是 'main' 或 'additional'，直接使用
      if (s.category === 'main' || s.category === 'additional') {
        return { ...s, category: s.category }
      }

      // 否則根據 serviceId 判斷：如果在主要服務列表中，則是 main；如果在額外服務列表中，則是 additional
      const isMainService = services.some(
        (svc) => svc._id === serviceId || svc.id === serviceId
      )
      const isAdditionalService = additionalServices.some(
        (as) =>
          as._id === serviceId ||
          as.id === serviceId ||
          as.serviceId === serviceId
      )

      const category = isMainService
        ? 'main'
        : isAdditionalService
        ? 'additional'
        : 'main' // 默認為 main

      return { ...s, category }
    })

    console.log('servicesWithCorrectCategory:', servicesWithCorrectCategory)

    const mainServicesFromCurrent = servicesWithCorrectCategory.filter(
      (s) => s.category === 'main'
    )
    console.log('mainServicesFromCurrent:', mainServicesFromCurrent)
    console.log(
      'currentServices 中每個項目的 category:',
      currentServices.map((s) => ({
        serviceId: s.serviceId,
        category: s.category
      }))
    )

    // 調試：檢查 services 數組的內容
    console.log('=== 調試：檢查 services 數組 ===')
    console.log('services 數組長度:', services.length)
    console.log(
      'services 數組內容:',
      services.map((svc) => ({
        _id: svc._id,
        id: svc.id,
        name: svc.name
      }))
    )
    console.log(
      '需要查找的 serviceIds:',
      mainServicesFromCurrent.map((s) => s.serviceId || s._id || s.id)
    )

    const fullMainServices = mainServicesFromCurrent
      .map((s) => {
        // 通過 serviceId 查找完整的服務對象（精確匹配）
        const serviceId = s.serviceId || s._id || s.id
        const found = services.find(
          (svc) => svc._id === serviceId || svc.id === serviceId
        )
        console.log(`查找主要服務 ID: ${serviceId}`, found)
        if (!found) {
          console.warn(
            `未找到主要服務 ID: ${serviceId}，使用 currentServices 中的數據構建服務對象`
          )
          console.log(
            '可用的 services:',
            services.map((svc) => ({
              _id: svc._id,
              id: svc.id,
              name: svc.name
            }))
          )
          // 如果找不到，使用 currentServices 中的數據構建一個完整的服務對象
          return {
            _id: serviceId,
            id: serviceId,
            serviceId: serviceId,
            name: s.serviceName || s.name,
            serviceName: s.serviceName || s.name,
            price: s.basePrice || s.price,
            basePrice: s.basePrice || s.price,
            duration: s.duration || 0,
            category: 'main'
          }
        }
        return found
      })
      .filter(Boolean)

    console.log('fullMainServices 最終結果:', fullMainServices)

    // 從完整的額外服務列表中查找匹配的額外服務
    const additionalServicesFromCurrent = servicesWithCorrectCategory.filter(
      (s) => s.category === 'additional'
    )
    console.log('additionalServicesFromCurrent:', additionalServicesFromCurrent)

    const fullAdditionalServices = additionalServicesFromCurrent
      .map((s) => {
        // 通過 serviceId 查找完整的服務對象（精確匹配）
        const serviceId = s.serviceId || s._id || s.id
        let found = additionalServices.find(
          (as) =>
            as._id === serviceId ||
            as.id === serviceId ||
            as.serviceId === serviceId
        )

        // 如果 ID 匹配失敗，嘗試通過服務名稱匹配
        if (!found && s.serviceName) {
          found = additionalServices.find((as) => as.name === s.serviceName)
        }

        console.log(`查找額外服務 ID: ${serviceId}`, found)
        if (!found) {
          console.warn(
            `未找到額外服務 ID: ${serviceId}，使用 currentServices 中的數據構建服務對象`
          )
          // 如果找不到，使用 currentServices 中的數據構建一個完整的服務對象
          return {
            _id: serviceId,
            id: serviceId,
            serviceId: serviceId,
            name: s.serviceName || s.name,
            serviceName: s.serviceName || s.name,
            price: s.basePrice || s.price,
            basePrice: s.basePrice || s.price,
            duration: s.duration || 0,
            category: 'additional'
          }
        }
        return found
      })
      .filter(Boolean)

    console.log('fullAdditionalServices 最終結果:', fullAdditionalServices)
    console.log('設置 selectedMainServices:', fullMainServices)
    console.log('設置 selectedAdditionalServices:', fullAdditionalServices)

    setSelectedMainServices(fullMainServices)
    setSelectedAdditionalServices(fullAdditionalServices)
  }, [currentServices, services, additionalServices])

  // 當服務數據載入完成後，初始化選擇狀態（參考 App 端實現）
  useEffect(() => {
    console.log('=== ServiceSelector 初始化 useEffect ===')
    console.log('visible:', visible)
    console.log('loading:', loading)
    console.log('services.length:', services.length)
    console.log('additionalServices.length:', additionalServices.length)
    console.log('currentServices:', currentServices)

    // 當 ServiceSelector 打開時，如果有 currentServices 且服務列表已加載完成（不在加載中），則初始化
    if (
      visible &&
      !loading &&
      currentServices &&
      currentServices.length > 0 &&
      (services.length > 0 || additionalServices.length > 0)
    ) {
      console.log('條件滿足，調用 initializeSelection')
      initializeSelection()
    } else if (
      visible &&
      currentServices &&
      currentServices.length > 0 &&
      loading
    ) {
      console.log('currentServices 有數據但服務列表正在加載，等待加載完成')
    } else if (visible && currentServices && currentServices.length > 0) {
      console.log('currentServices 有數據但服務列表未加載，等待加載完成')
    } else if (visible) {
      console.log('條件不滿足，不初始化')
    }
  }, [
    visible,
    loading,
    services,
    additionalServices,
    currentServices,
    initializeSelection
  ])

  // 獲取服務數據
  useEffect(() => {
    if (visible && businessId) {
      fetchServices()
    }
  }, [visible, businessId])

  const fetchServices = async () => {
    setLoading(true)
    try {
      const [servicesResponse, additionalServicesResponse] = await Promise.all([
        api.get(`/api/business_B/${businessId}/products`),
        api.get(`/api/business_B/${businessId}/additional-services`)
      ])

      setServices(servicesResponse.data || [])
      setAdditionalServices(additionalServicesResponse.data?.data || [])
    } catch (error) {
      console.error('獲取服務數據失敗:', error)
      message.error('獲取服務數據失敗')
    } finally {
      setLoading(false)
    }
  }

  // 過濾服務
  const filteredServices = services.filter(
    (service) =>
      service.name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchKeyword.toLowerCase())
  )

  const filteredAdditionalServices = additionalServices.filter(
    (service) =>
      service.name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchKeyword.toLowerCase())
  )

  // 選擇主要服務（多選切換）
  const handleMainServiceSelect = async (service) => {
    const exists = selectedMainServices.some((s) => s._id === service._id)
    const newMainServices = exists
      ? selectedMainServices.filter((s) => s._id !== service._id)
      : [...selectedMainServices, service]
    setSelectedMainServices(newMainServices)
    await triggerPriceCalculation(newMainServices, selectedAdditionalServices)
  }

  // 選擇額外服務（多選切換）
  const handleAdditionalServiceSelect = async (service) => {
    const isSelected = selectedAdditionalServices.some(
      (s) => s._id === service._id
    )
    const newAdditionalServices = isSelected
      ? selectedAdditionalServices.filter((s) => s._id !== service._id)
      : [...selectedAdditionalServices, service]
    setSelectedAdditionalServices(newAdditionalServices)
    await triggerPriceCalculation(selectedMainServices, newAdditionalServices)
  }

  // 觸發價格計算（預約情境下主服務與加選分開傳，加選清空時須傳 []）
  const triggerPriceCalculation = async (mainServices, additionalSvcs) => {
    if (!onPriceCalculation) return
    try {
      const mainIds = mainServices.map((s) => s._id).filter(Boolean)
      const additionalIds = additionalSvcs.map((s) => s._id).filter(Boolean)
      const priceCalculation = await calculatePrice(mainIds, additionalIds)
      if (priceCalculation) {
        onPriceCalculation(priceCalculation)
      }
    } catch (error) {
      console.error('價格計算失敗，但不影響服務選擇:', error)
    }
  }

  // 檢查服務是否已選
  const isMainServiceSelected = (service) => {
    return selectedMainServices.some((s) => s._id === service._id)
  }

  const isAdditionalServiceSelected = (service) => {
    return selectedAdditionalServices.some((s) => s._id === service._id)
  }

  // 價格計算功能（有 bookingId 時與後端約定：selectedServices=主服務、additionalServices=加選）
  const calculatePrice = async (mainIds, additionalIds = []) => {
    try {
      const apiEndpoint = bookingId
        ? `/api/business/bookings/${bookingId}/calculate-price`
        : '/api/business/calculate-price'

      const body = bookingId
        ? {
            selectedServices: mainIds || [],
            additionalServices: additionalIds || []
          }
        : {
            selectedServices: [
              ...(mainIds || []),
              ...(additionalIds || [])
            ]
          }

      const response = await api.post(apiEndpoint, body)

      if (response.data.success) {
        return response.data.priceCalculation
      } else {
        throw new Error(response.data.message || '價格計算失敗')
      }
    } catch (error) {
      console.error('價格計算失敗:', error)
      const mainService = selectedMainServices?.length
        ? selectedMainServices[0]
        : null
      const additionalServicesForLocal = selectedAdditionalServices || []
      return calculateLocalPrice(mainService, additionalServicesForLocal)
    }
  }

  // 本地價格計算
  const calculateLocalPrice = (mainService, additionalServicesList) => {
    const basePrice = mainService?.price || 0
    const additionalPrice = additionalServicesList.reduce(
      (sum, service) => sum + service.price,
      0
    )
    const totalDuration =
      (mainService?.duration || 0) +
      additionalServicesList.reduce((sum, service) => sum + service.duration, 0)

    return {
      originalTotal: basePrice + additionalPrice,
      finalPrice: basePrice + additionalPrice,
      totalDuration,
      serviceDetails: mainService
        ? [
            {
              serviceName: mainService.name,
              price: mainService.price,
              duration: mainService.duration
            }
          ]
        : [],
      additionalServicesDetails: additionalServicesList.map((service) => ({
        serviceName: service.name,
        price: service.price,
        duration: service.duration
      }))
    }
  }

  // 計算總價和總時長
  const calculateTotal = () => {
    const mainPrice = selectedMainServices.reduce(
      (sum, service) => sum + (service.price || 0),
      0
    )
    const mainDuration = selectedMainServices.reduce(
      (sum, service) => sum + (service.duration || 0),
      0
    )

    const additionalPrice = selectedAdditionalServices.reduce(
      (sum, service) => sum + (service.price || 0),
      0
    )
    const additionalDuration = selectedAdditionalServices.reduce(
      (sum, service) => sum + (service.duration || 0),
      0
    )

    return {
      totalPrice: mainPrice + additionalPrice,
      totalDuration: mainDuration + additionalDuration
    }
  }

  const { totalPrice, totalDuration } = calculateTotal()

  // 確認選擇
  const handleConfirm = async () => {
    if (selectedMainServices.length === 0) {
      message.warning('請至少選擇一個主要服務')
      return
    }

    try {
      const mainIds = selectedMainServices.map((s) => s._id).filter(Boolean)
      const additionalIds = selectedAdditionalServices
        .map((s) => s._id)
        .filter(Boolean)

      const priceCalculation = await calculatePrice(mainIds, additionalIds)

      const services = [
        ...selectedMainServices.map((service) => ({
          serviceId: service._id,
          serviceName: service.name,
          basePrice: service.price,
          duration: service.duration,
          category: 'main'
        })),
        ...selectedAdditionalServices.map((service) => ({
          serviceId: service._id,
          serviceName: service.name,
          price: service.price,
          duration: service.duration,
          category: 'additional'
        }))
      ]

      // 將服務選擇結果傳遞給父組件
      onServicesSelected({
        services,
        selectedServiceIds: [...mainIds, ...additionalIds],
        discountedPrice: priceCalculation?.finalPrice || totalPrice,
        suggestedTotal: priceCalculation?.originalTotal || totalPrice,
        totalDuration: priceCalculation?.totalDuration || totalDuration,
        priceCalculation
      })

      // 如果有價格計算回調函數，調用它
      if (onPriceCalculation && priceCalculation) {
        onPriceCalculation(priceCalculation)
      }

      onClose()
    } catch (error) {
      console.error('價格計算失敗:', error)
      message.error('價格計算失敗，請重試')
    }
  }

  // 重置選擇
  const handleReset = () => {
    setSelectedMainServices([])
    setSelectedAdditionalServices([])
  }

  const renderServiceCard = (service, isMain = true) => {
    const isSelected = isMain
      ? isMainServiceSelected(service)
      : isAdditionalServiceSelected(service)

    return (
      <Col xs={24} sm={12} md={8} lg={6} key={service._id}>
        <ServiceCard
          selected={isSelected}
          onClick={() =>
            isMain
              ? handleMainServiceSelect(service)
              : handleAdditionalServiceSelect(service)
          }
        >
          {isSelected && (
            <SelectionIndicator>
              <CheckOutlined />
            </SelectionIndicator>
          )}

          <ServiceInfo>
            <ServiceName selected={isSelected}>{service.name}</ServiceName>
            <ServicePrice selected={isSelected}>${service.price}</ServicePrice>
          </ServiceInfo>
          <ServiceDuration selected={isSelected}>
            {service.duration} 分鐘
          </ServiceDuration>
        </ServiceCard>
      </Col>
    )
  }

  const renderContent = () => (
    <div>
      {/* 總計卡片 */}
      <TotalCard>
        <TotalInfo>
          <div>
            <TotalLabel>總價格</TotalLabel>
            <TotalValue>${totalPrice}</TotalValue>
          </div>
          <div>
            <TotalLabel>總時長</TotalLabel>
            <TotalValue>{totalDuration} 分鐘</TotalValue>
          </div>
        </TotalInfo>
      </TotalCard>

      {/* 搜索框 */}
      <SearchContainer>
        <Input
          placeholder='搜尋服務名稱、類別或描述...'
          prefix={<SearchOutlined />}
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
        />
      </SearchContainer>

      {/* 主要服務 */}
      <CategoryTitle>主要服務項目</CategoryTitle>
      <Row gutter={[16, 16]}>
        {filteredServices.map((service) => renderServiceCard(service, true))}
      </Row>

      {/* 額外服務 */}
      <CategoryTitle>額外服務項目</CategoryTitle>
      <Row gutter={[16, 16]}>
        {filteredAdditionalServices.map((service) =>
          renderServiceCard(service, false)
        )}
      </Row>
    </div>
  )

  return (
    <ResponsiveContainer>
      {/* 手機版 - Drawer */}
      <Drawer
        title='批量選擇服務'
        placement='bottom'
        height='80%'
        open={visible && isMobile}
        onClose={onClose}
        extra={
          <Button type='primary' onClick={handleConfirm}>
            確認
          </Button>
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size='large' />
          </div>
        ) : (
          renderContent()
        )}
      </Drawer>

      {/* 電腦版 - Modal */}
      <Modal
        title='批量選擇服務'
        open={visible && !isMobile}
        onCancel={onClose}
        width={800}
        footer={[
          <Button key='reset' onClick={handleReset}>
            重置
          </Button>,
          <Button key='cancel' onClick={onClose}>
            取消
          </Button>,
          <Button key='confirm' type='primary' onClick={handleConfirm}>
            確認
          </Button>
        ]}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size='large' />
          </div>
        ) : (
          renderContent()
        )}
      </Modal>
    </ResponsiveContainer>
  )
}

export default ServiceSelector
